import React from 'react'
import { Card, Table, Button, Checkbox, DatePicker, Input } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import moment from 'moment'
import users from '@services/users'
import FilterIcon from '@assets/svg/filter.svg'

const MESSAGE_PREVIEW_LENGTH = 130
const TYPE_OPTIONS = ['SMS', 'EMAIL']

type UserMessagesProps = {
    clientInfo: {
        id: number | string
    }
    purchaseStatusVersion?: number
}

type UserMessagesState = {
    messages: any
    typeCounts: { type: string; count: number }[]
    loading: boolean
    expandedRows: { [key: string]: boolean }
    typeFilter: string | null
    dateFilter: string | null
    textFilter: string
}

class UserMessages extends React.Component<UserMessagesProps, UserMessagesState> {
    constructor(props) {
        super(props)
        this.state = {
            messages: null,
            typeCounts: [],
            loading: true,
            expandedRows: {},
            typeFilter: null,
            dateFilter: null,
            textFilter: ''
        }
    }

    _isMounted = false
    _tableRef = React.createRef<HTMLDivElement>()
    _searchTimer: any = null

    // single fetch path: reads the active filters from state and sends them to the API
    fetchMessages = (page = 1) => {
        const { typeFilter, dateFilter, textFilter } = this.state
        users
            .getUserMessages({
                user_id: this.props.clientInfo.id,
                page,
                type: typeFilter || undefined,
                date: dateFilter || undefined,
                search: textFilter || undefined
            })
            .then(res => {
                if (!this._isMounted) return
                this.setState({
                    messages: res.data.messages,
                    typeCounts: res.data.typeCounts || [],
                    loading: false
                })
            })
            .catch(error => {
                console.error('Failed to fetch user messages', error)
                if (!this._isMounted) return
                this.setState({ loading: false })
            })
    }

    // called by the table when the user changes page
    paginate = async ({ page }: { page: any }) => {
        this.fetchMessages(page.current)
        return false
    }

    // re-fetch from page 1 whenever a filter changes
    applyFilters = () => {
        this.setState({ loading: true }, () => this.fetchMessages(1))
    }

    componentDidMount() {
        this._isMounted = true
        this.getMessages()
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.clientInfo && prevProps.clientInfo.id
        const currentId = this.props.clientInfo && this.props.clientInfo.id
        const purchaseVersionChanged = this.props.purchaseStatusVersion !== prevProps.purchaseStatusVersion

        if (currentId && (prevId !== currentId || purchaseVersionChanged)) {
            this.setState({ loading: true }, () => {
                this.fetchMessages(1)
            })
        }
    }

    getMessages = () => {
        this.fetchMessages(1)
    }

    // render message text; lines with no Hebrew letters (e.g. a URL) are forced LTR
    renderMessageText = (text: string) => {
        const hasHebrew = (line: string) => /[֐-׿]/.test(line)
        const lines = text.split('\n')
        return lines.map((line, i) => {
            const ltr = line.trim().length > 0 && !hasHebrew(line)
            return (
                <React.Fragment key={i}>
                    {ltr ? (
                        <span style={{ direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block' }}>{line}</span>
                    ) : (
                        line
                    )}
                    {i < lines.length - 1 ? '\n' : null}
                </React.Fragment>
            )
        })
    }

    render() {
        const { loading, messages, typeCounts, expandedRows, typeFilter, dateFilter, textFilter } = this.state
        const isAll = typeFilter === null

        const filterDropdown = () => (
            <div style={{ padding: 8, direction: 'rtl' }}>
                <div
                    style={{
                        borderBottom: '1px solid #e8e8e8',
                        paddingBottom: 6,
                        marginBottom: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 6
                    }}
                    onClick={() => {
                        this.setState({ typeFilter: null }, this.applyFilters)
                    }}
                >
                    <Checkbox checked={isAll} />
                    <span>הכל</span>
                </div>
                {TYPE_OPTIONS.map(opt => (
                    <div
                        key={opt}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 0'
                        }}
                        onClick={() => {
                            this.setState({ typeFilter: opt }, this.applyFilters)
                        }}
                    >
                        <Checkbox checked={typeFilter === opt} />
                        <span>{opt}</span>
                    </div>
                ))}
            </div>
        )

        // date filter dropdown: pick from calendar or type the date manually
        const dateFilterDropdown = () => (
            <div style={{ padding: 8, direction: 'rtl' }}>
                <DatePicker
                    // controlled by dateFilter (stored as YYYY-MM-DD); allows typing too
                    value={dateFilter ? moment(dateFilter, 'YYYY-MM-DD') : undefined}
                    format="DD/MM/YYYY"
                    placeholder="בחר תאריך"
                    style={{ width: '100%' }}
                    onChange={d => {
                        this.setState({ dateFilter: d ? d.format('YYYY-MM-DD') : null }, this.applyFilters)
                    }}
                />
                <div
                    style={{ marginTop: 6, cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => {
                        this.setState({ dateFilter: null }, this.applyFilters)
                    }}
                >
                    נקה
                </div>
            </div>
        )

        // text search dropdown: filters by free text typed into the box
        const textFilterDropdown = () => (
            <div style={{ padding: 8, direction: 'rtl' }}>
                <Input
                    value={textFilter}
                    placeholder="חיפוש טקסט"
                    style={{ width: 200 }}
                    onChange={e => {
                        const value = e.target.value
                        // update the box immediately, but debounce the server fetch
                        this.setState({ textFilter: value }, () => {
                            if (this._searchTimer) clearTimeout(this._searchTimer)
                            this._searchTimer = setTimeout(this.applyFilters, 400)
                        })
                    }}
                />
                <div
                    style={{ marginTop: 6, cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => {
                        if (this._searchTimer) clearTimeout(this._searchTimer)
                        this.setState({ textFilter: '' }, this.applyFilters)
                    }}
                >
                    נקה
                </div>
            </div>
        )

        // the server already applies all filters, so just render what it returns
        const filteredData = messages ? messages.data : []
        // total reflects the active type filter via typeCounts; otherwise the full filtered total
        const typeCountEntry = typeFilter ? typeCounts.find((c: any) => c.type === typeFilter) : null
        const filteredTotal = typeFilter
            ? (typeCountEntry ? typeCountEntry.count : 0)
            : (messages ? messages.total : 0)

        const columns: ColumnProps<any>[] = [
            {
                title: 'נשלח בתאריך',
                key: 'created_at',
                align: 'center',
                width: 150,
                className: 'col-type-filter',
                filterDropdown: dateFilterDropdown,
                filterIcon: () => (
                    <span
                        className={`table-filter-icon ${dateFilter ? 'table-filter-icon-active' : ''}`}
                        aria-hidden="true"
                    >
                        <FilterIcon />
                    </span>
                ),
                render: (_: any, record: any) => <span>{moment(record.created_at).format('DD/MM/YYYY HH:mm')}</span>
            },
            {
                title: 'נשלח באמצעות',
                key: 'type',
                align: 'center',
                width: 150,
                dataIndex: 'type',
                className: 'col-type-filter',
                filterDropdown,
                filterIcon: () => (
                    <span
                        className={`table-filter-icon ${typeFilter ? 'table-filter-icon-active' : ''}`}
                        aria-hidden="true"
                    >
                        <FilterIcon />
                    </span>
                )
            },
            {
                title: 'מספר/כתובת',
                key: 'phone',
                align: 'center',
                width: 150,
                render: (_: any, record: any) => {
                    return <span>{`0${record.phone}`}</span>
                }
            },
            {
                title: 'תוכן',
                key: 'message',
                align: 'right',
                className: 'col-type-filter',
                filterDropdown: textFilterDropdown,
                filterIcon: () => (
                    <span
                        className={`table-filter-icon ${textFilter ? 'table-filter-icon-active' : ''}`}
                        aria-hidden="true"
                    >
                        <FilterIcon />
                    </span>
                ),
                render: (_: any, record: any) => {
                    const msg: string = record.message || ''
                    const isLong = msg.length > MESSAGE_PREVIEW_LENGTH
                    const expanded = expandedRows[record.id]
                    return (
                        <div
                            style={{
                                wordBreak: 'break-word',
                                // collapsed: keep everything on one line; expanded: preserve new lines
                                whiteSpace: expanded ? 'pre-wrap' : 'nowrap'
                            }}
                        >
                            {isLong && !expanded
                                ? this.renderMessageText(msg.slice(0, MESSAGE_PREVIEW_LENGTH) + '...')
                                : this.renderMessageText(msg)}
                            {/* collapse button sits on its own block line; no inline shift, no animation jump */}
                            {isLong && expanded && (
                                <div style={{ marginTop: 6 }}>
                                    <Button
                                        size="small"
                                        style={{
                                            padding: '0 12px',
                                            height: 24,
                                            borderRadius: 12,
                                            border: '1px solid #fa8c16',
                                            color: '#fa8c16',
                                            fontSize: 12,
                                            lineHeight: '22px',
                                            transition: 'none'
                                        }}
                                        onClick={() => {
                                            this.setState(prev => ({
                                                expandedRows: Object.assign({}, prev.expandedRows, {
                                                    [record.id]: !prev.expandedRows[record.id]
                                                })
                                            }))
                                        }}
                                    >
                                        צמצם
                                    </Button>
                                </div>
                            )}
                            {/* expand button stays inline at end of the single-line text */}
                            {isLong && !expanded && (
                                <Button
                                    size="small"
                                    style={{
                                        display: 'inline-block',
                                        marginInlineStart: 8,
                                        padding: '0 12px',
                                        height: 24,
                                        borderRadius: 12,
                                        border: '1px solid #fa8c16',
                                        color: '#fa8c16',
                                        fontSize: 12,
                                        lineHeight: '22px',
                                        verticalAlign: 'middle',
                                        transition: 'none'
                                    }}
                                    onClick={() => {
                                        this.setState(prev => ({
                                            expandedRows: Object.assign({}, prev.expandedRows, {
                                                [record.id]: !prev.expandedRows[record.id]
                                            })
                                        }))
                                    }}
                                >
                                    הרחב
                                </Button>
                            )}
                        </div>
                    )
                }
            }
        ]

        return (
            <Card title="הודעות משתמש" headStyle={{ backgroundColor: '#dadce0' }}>
                <style>{`
                    .user-messages-table .ant-table-placeholder {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    /* keep the header cell a normal table cell so all columns stay in one row.
                       center its inline content; title + icon flow inline on the same line. */
                    .user-messages-table th.col-type-filter.ant-table-column-has-actions.ant-table-column-has-filters {
                        text-align: center;
                        white-space: nowrap;
                        padding-top: 0px;
                        padding-bottom: 0px;
                    }
                    /* title and icon both inline, vertically centered on the same line */
                    .user-messages-table th.col-type-filter .ant-table-header-column {
                        display: inline-block;
                        vertical-align: middle;
                    }
                    /* icon: full cell height so the hover/click area covers the whole height */
                    .user-messages-table th.col-type-filter .ant-table-filter-icon,
                    .user-messages-table th.col-type-filter .table-filter-icon {
                        position: relative !important;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        vertical-align: middle;
                        height: 54px;
                        z-index: 10;
                        margin: 0 5px 0 0;
                        padding: 0;
                    }
                    .user-messages-table th.col-type-filter .table-filter-icon {
                        color: rgba(0, 0, 0, 0.45);
                    }
                    .user-messages-table th.col-type-filter .table-filter-icon.ant-dropdown-trigger:hover,
                    .user-messages-table th.col-type-filter .table-filter-icon:hover,
                    .user-messages-table th.col-type-filter .table-filter-icon.ant-dropdown-open {
                        color: #1890ff !important;
                    }
                    .user-messages-table th.col-type-filter .table-filter-icon:hover svg,
                    .user-messages-table th.col-type-filter .table-filter-icon:hover svg path,
                    .user-messages-table th.col-type-filter .table-filter-icon.ant-dropdown-open svg,
                    .user-messages-table th.col-type-filter .table-filter-icon.ant-dropdown-open svg path {
                        fill: #1890ff;
                    }
                    .user-messages-table th.col-type-filter .table-filter-icon.table-filter-icon-active {
                        color: #1890ff;
                    }
                    .user-messages-table th.col-type-filter .ant-table-filter-icon > svg,
                    .user-messages-table th.col-type-filter .table-filter-icon > svg {
                        position: relative !important;
                        top: auto !important;
                        left: auto !important;
                        margin: 0 !important;
                        width: 14px;
                        height: 14px;
                    }
                    .ant-table-filter-dropdown {
                        z-index: 9999 !important;
                    }
                `}</style>
                <div ref={this._tableRef} style={{ position: 'relative' }}>
                    {messages ? (
                        <Table
                            className="user-messages-table"
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            dataSource={filteredData}
                            getPopupContainer={() => this._tableRef.current || document.body}
                            pagination={{ total: filteredTotal, pageSize: messages.perPage, hideOnSinglePage: true }}
                            onChange={page => {
                                this.setState({ loading: true })
                                this.paginate({ page })
                            }}
                        />
                    ) : (
                        ''
                    )}
                </div>
            </Card>
        )
    }
}

export default UserMessages
